
import Stepper from 'primevue/stepper';
import StepList from 'primevue/steplist';
import StepPanels from 'primevue/steppanels';
import StepItem from 'primevue/stepitem';
import Step from 'primevue/step';
import StepPanel from 'primevue/steppanel';

import Card from 'primevue/card';
import ProgressSpinner from 'primevue/progressspinner';

import BankDetail from '../card/BankDetail.vue';
import Button from 'primevue/button';
import Toast from 'primevue/toast';

import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { useAuthStore } from '../../stores/auth';
import { storeToRefs } from 'pinia';
import { depositWithdraw } from '../../apis/deposit_withdraw';
import { BankDepositResult } from '../../types/deposit_withdraw';
import { useWebStore } from '../../stores/web';
import { useToast } from 'primevue/usetoast';
import { showVuePopup } from '../../utils/showVuePopup';
import WalletUpdate from '../../popup/WalletUpdate.vue';
import { useSession } from '../../stores/session';

export default defineComponent({
	name: 'DepositTrueWallet',
	components: {
		BankDetail,
		Button,
		Stepper,
		StepList,
		StepPanels,
		StepItem,
		Step,
		StepPanel,
		Card,
		ProgressSpinner,
		Toast,
	},
	setup() {
		const auth = useAuthStore();
		auth.hydrate();
		const { profile } = storeToRefs(auth);

		const web = useWebStore();
		web.hydrate();
		const { webPermission } = storeToRefs(web);

		const toast = useToast();
		const session = useSession();

		const stepper = ref<string>('1');

		// เปิด/ปิดช่องทาง TrueWallet แบบ reactive
		const isActive = computed<boolean>(() => webPermission.value?.isDeposit?.isTrueWallet ?? false);

		// ต้องอัปเดตบัญชีธนาคารก่อนหรือไม่
		const isBankUpdate = computed<boolean>(() => {
			const hasAcc = !!profile.value?.customerBankAccount;
			const hasName = !!profile.value?.customerBankName;
			return !(hasAcc && hasName);
		});

		const bankAccount = ref({
			data: {
				fullName: `${profile.value?.firstName ?? ''} ${profile.value?.lastName ?? ''}`.trim(),
				bankName: 'TRUEWALLET',
				bankNumber: profile.value?.phoneNo ?? '',
				bankCode: 'TRUEWALLET',
			},
			// ให้ active เฉพาะเมื่อมีเบอร์โทร (สำหรับ TrueWallet)
			active: !!(profile.value?.phoneNo),
			loading: false,
		});

		// profile เปลี่ยน → sync การ์ดฝั่งผู้ใช้
		watch(profile, (p) => {
			bankAccount.value.data.fullName = `${p?.firstName ?? ''} ${p?.lastName ?? ''}`.trim();
			bankAccount.value.data.bankNumber = p?.phoneNo ?? '';
			bankAccount.value.active = !!p?.phoneNo;
		}, { immediate: true });

		const bankDeposit = ref({
			data: {
				fullName: '',
				bankName: '',
				bankNumber: '',
				bankCode: '',
			},
			active: false,
			loading: false,
		});

		const handleDeposit = async () => {
			bankDeposit.value.loading = true;
			try {
				const resp = await depositWithdraw.getBankDeposit({ accountType: 'TRUEWALLET', limit: 1 });

				if (resp.code === '8017') {
					alert(resp.message || 'กรุณาเข้าสู่ระบบใหม่');
					await auth.logout();
					window.location.reload();
					return;
				}
				if (resp.code !== '0') throw new Error(resp.message);

				const result = resp.result as BankDepositResult[] | undefined;

				if (!Array.isArray(result) || result.length < 1) {
					bankDeposit.value.active = false;
					throw new Error('ไม่พบบัญชีธนาคาร กรุณาติดต่อ Support');
				}

				const data = result[0];
				bankDeposit.value.data = {
					fullName: data.accountName,
					bankName: data.bankName,
					bankNumber: data.accountNumber,
					bankCode: data.accountBankName,
				};
				bankDeposit.value.active = true;
			} catch (error: any) {
				toast.add({
					severity: 'error',
					summary: 'เกิดข้อผิดพลาดบางอย่าง',
					detail: error?.message || String(error),
					life: 3000,
				});
			} finally {
				bankDeposit.value.loading = false;
			}
		};

		onMounted(() => {
			handleDeposit();
		});

		// debug เปลี่ยนสเตป
		watch(stepper, (v) => console.log('stepper:', v));

		const btnClickUpdateBank = async () => {
			session.set('navbar_primary', 'wallet');
			session.set('navbar_sub', 'wallet:update');
			await showVuePopup(WalletUpdate, { tabs: 'bank' });
		};

		return {
			profile,
			stepper,
			bankAccount,
			bankDeposit,
			isActive,
			isBankUpdate,
			btnClickUpdateBank,
		};
	},
});
